import React, { useEffect, useState } from "react";
import "./sharedCatalogue.css";

interface SharedCatalogueChromeProps {
  organizationName?: string;
  subtitle?: string;
  pillText?: string;
  /** Sign in / account actions (right side of bar, beside pill). */
  headerActions?: React.ReactNode;
  /** Text in the square mark. Null shows a neutral mark (a reseller's client never sees "HK"). */
  mark?: string | null;
  footerText?: React.ReactNode;
  /** Pinned to the bottom of the screen (reseller share bar). */
  bottomBar?: React.ReactNode;
  children: React.ReactNode;
}

const SharedCatalogueChrome: React.FC<SharedCatalogueChromeProps> = ({
  organizationName = "Collection",
  subtitle = "Curated for you",
  pillText = "Private link",
  headerActions,
  mark = "HK",
  footerText,
  bottomBar,
  children,
}) => {
  const showPill = Boolean(pillText && String(pillText).trim());
  const [compact, setCompact] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const collapsed = compact && !menuOpen;

  useEffect(() => {
    let lastY = window.scrollY || 0;
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop;
      if (y < 28) {
        setCompact(false);
        setMenuOpen(false);
        lastY = y;
        return;
      }
      if (y > lastY + 8) {
        setCompact(true);
        setMenuOpen(false);
      } else if (y < lastY - 8) {
        setCompact(false);
      }
      lastY = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className={`hkp-shared-page${bottomBar ? " has-bottom-bar" : ""}`}>
      <header className={`hkp-shared-bar${collapsed ? " is-compact" : ""}`}>
        <div className="hkp-shared-bar-inner">
          <div className="hkp-shared-brand">
            <button
              type="button"
              className={`hkp-shared-mark${mark ? "" : " is-neutral"}`}
              aria-label={collapsed ? "Show collection menu" : undefined}
              aria-expanded={compact ? !collapsed : undefined}
              onClick={() => {
                if (compact) setMenuOpen((open) => !open);
              }}
            >
              {mark || <span aria-hidden="true">✦</span>}
            </button>
            <div className="hkp-shared-brand-text">
              <div className="hkp-shared-org" title={organizationName}>
                {organizationName}
              </div>
              {subtitle ? (
                <div className="hkp-shared-sub" title={subtitle}>
                  {subtitle}
                </div>
              ) : null}
            </div>
          </div>
          <div className="hkp-shared-bar-mid">
            {showPill ? <span className="hkp-shared-pill">{pillText}</span> : null}
          </div>
          {headerActions ? (
            <div className="hkp-shared-bar-right">
              <div className="hkp-shared-header-actions">{headerActions}</div>
            </div>
          ) : (
            <div className="hkp-shared-bar-right" />
          )}
        </div>
      </header>
      <div className="hkp-shared-main">{children}</div>
      <footer className="hkp-shared-footer">
        <div className="hkp-shared-footer-inner">
          <p className="hkp-shared-footer-text">
            {footerText ?? (
              <>
                <strong>HastKala Pramanak</strong> — authenticity and traceability for
                handloom and handicraft.
              </>
            )}
          </p>
        </div>
      </footer>
      {bottomBar}
    </div>
  );
};

export default SharedCatalogueChrome;
